import { useEffect, useState } from "react";
import { ChevronDownIcon, XIcon } from "lucide-react";
import type { PayrollPositionDraft } from "../../types";

type PositionFormProps = {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  initialValue?: PayrollPositionDraft | null;
  onSave?: (input: PayrollPositionDraft) => void | Promise<void>;
};

const today = () => new Date().toISOString().split("T")[0] ?? "";

export function PositionForm({
  isOpen,
  onClose,
  employeeName,
  initialValue,
  onSave,
}: PositionFormProps) {
  const [title, setTitle] = useState("");
  const [track, setTrack] =
    useState<NonNullable<PayrollPositionDraft["track"]>>("Judaic Studies");
  const [compType, setCompType] =
    useState<NonNullable<PayrollPositionDraft["compensationType"]>>(
      "Annual Salary",
    );
  const [amount, setAmount] = useState("");
  const [fte, setFte] = useState("100");
  const [distribution, setDistribution] =
    useState<NonNullable<PayrollPositionDraft["payDistribution"]>>(
      "Spread Evenly",
    );
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [parsonageExpanded, setParsonageExpanded] = useState(false);
  const [parsonageEnabled, setParsonageEnabled] = useState(false);
  const [parsonageAmount, setParsonageAmount] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setTitle(initialValue?.title ?? "");
    setTrack(initialValue?.track ?? "Judaic Studies");
    setCompType(initialValue?.compensationType ?? "Annual Salary");
    setAmount(
      initialValue?.amount == null ? "" : String(initialValue.amount),
    );
    setFte(
      initialValue?.ftePercentage == null
        ? "100"
        : String(initialValue.ftePercentage),
    );
    setDistribution(initialValue?.payDistribution ?? "Spread Evenly");
    setEffectiveDate(initialValue?.effectiveDate ?? today());
    setParsonageEnabled(Boolean(initialValue?.parsonageEnabled));
    setParsonageExpanded(Boolean(initialValue?.parsonageEnabled));
    setParsonageAmount(
      initialValue?.parsonageAmount == null
        ? ""
        : String(initialValue.parsonageAmount),
    );
  }, [initialValue, isOpen]);

  if (!isOpen) {
    return null;
  }

  const isValid =
    title !== "" && amount !== "" && Number(amount) > 0 && Number(fte) > 0;
  const showParsonage =
    compType === "Annual Salary" || compType === "Monthly Salary";

  const handleSave = async () => {
    if (!isValid) {
      return;
    }
    await onSave?.({
      ...initialValue,
      title,
      track,
      compensationType: compType,
      amount: Number(amount),
      ftePercentage: Number(fte),
      payDistribution: distribution,
      effectiveDate,
      parsonageEnabled: showParsonage ? parsonageEnabled : undefined,
      parsonageAmount:
        showParsonage && parsonageEnabled && parsonageAmount !== ""
          ? Number(parsonageAmount)
          : undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              {initialValue?.id ? "Edit Position" : "Add Position"}
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              For {employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-6">
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Position Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Grade 4 Judaic Studies Teacher"
              data-testid="payroll-position-title"
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Track
              </label>
              <select
                value={track}
                onChange={(event) =>
                  setTrack(
                    event.target.value as NonNullable<PayrollPositionDraft["track"]>,
                  )
                }
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="Judaic Studies">Judaic Studies</option>
                <option value="General Studies">General Studies</option>
                <option value="Administrative">Administrative</option>
                <option value="Support">Support</option>
                <option value="N/A">N/A</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Compensation Type
              </label>
              <select
                value={compType}
                onChange={(event) =>
                  setCompType(
                    event.target
                      .value as NonNullable<
                      PayrollPositionDraft["compensationType"]
                    >,
                  )
                }
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="Annual Salary">Annual Salary</option>
                <option value="Monthly Salary">Monthly Salary</option>
                <option value="Hourly Rate">Hourly Rate</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0.00"
                  data-testid="payroll-position-amount"
                  className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                FTE Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={fte}
                  onChange={(event) => setFte(event.target.value)}
                  className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-3 pr-8 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                  %
                </span>
              </div>
            </div>
          </div>

          {showParsonage ? (
            <div className="overflow-hidden rounded-[8px] border border-border">
              <button
                type="button"
                onClick={() => setParsonageExpanded((current) => !current)}
                className="flex w-full items-center justify-between bg-muted/30 p-4 transition-colors hover:bg-muted/50"
              >
                <span className="font-body text-sm font-medium text-iron-grey">
                  Parsonage (Clergy Housing Allowance)
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 text-text-secondary transition-transform duration-200 ${
                    parsonageExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>

              {parsonageExpanded ? (
                <div className="space-y-4 bg-surface-light p-4 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between">
                    <label className="font-body text-sm font-medium text-iron-grey">
                      Parsonage Enabled
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setParsonageEnabled((current) => !current)
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        parsonageEnabled ? "bg-primary" : "bg-border"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                          parsonageEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {parsonageEnabled ? (
                    <div className="animate-in slide-in-from-top-2 duration-200">
                      <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                        Annual Parsonage Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                          $
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={parsonageAmount}
                          onChange={(event) =>
                            setParsonageAmount(event.target.value)
                          }
                          placeholder="0.00"
                          className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <p className="mt-2 font-body text-xs leading-relaxed text-text-secondary">
                        Tax-exempt housing allowance under IRS Section 107. This
                        amount is excluded from W-2 wages and exported as a
                        separate column.
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Pay Distribution
              </label>
              <select
                value={distribution}
                onChange={(event) =>
                  setDistribution(
                    event.target
                      .value as NonNullable<
                      PayrollPositionDraft["payDistribution"]
                    >,
                  )
                }
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="Spread Evenly">Spread Evenly</option>
                <option value="Custom Schedule">Custom Schedule</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Effective Date
              </label>
              <input
                type="date"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={() => void handleSave()}
            data-testid="payroll-position-save"
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save Position
          </button>
        </div>
      </div>
    </div>
  );
}
